<template>
  <div class="page-container">
    <div class="page-header">
      <picture class="banner-picture">
        <source media="(max-width: 768px)" srcset="@/assets/img/banner/report_m.jpg" />
        <img class="banner-bg" src="@/assets/img/banner/report.jpg" alt="" />
      </picture>
      <div class="banner-overlay"></div>
      <div class="container">
        <h1>报告查真伪</h1>
        <p class="subtitle">输入报告编号，快速验证报告真伪</p>
      </div>
    </div>
    
    <div class="container content">
      <div class="verification-box">
        <div v-if="!isDetailMode">
          <div class="icon-wrapper">
            <span class="search-icon">🔍</span>
          </div>
          <h2>请输入报告编号</h2>
          <p class="desc">请查看检测报告封面或右上角的唯一编号</p>
          
          <div class="search-form">
            <input 
              type="text" 
              v-model="reportId" 
              placeholder="请输入报告编号 (例如: R20231015001)" 
              class="search-input"
              @keyup.enter="handleVerify"
            />
            <button @click="handleVerify" class="search-btn">立即查询</button>
          </div>
          
          <div class="tips-box">
            <h3>温馨提示</h3>
            <ul>
              <li>请仔细核对报告编号，区分大小写</li>
              <li>系统仅支持查询近三年内出具的检测报告</li>
              <!-- <li>如查询结果与实物不符，请立即联系客服人员</li> -->
              <!-- <li>客服热线：400-999-1636 (工作日 9:00-18:00)</li> -->
            </ul>
          </div>
        </div>
        <div v-else class="detail-top">
          <div class="detail-pill">扫码查询结果</div>
          <!-- <router-link to="/verification" class="detail-link">返回查询</router-link> -->
        </div>

        <div v-if="loading" class="result-box">
          <p>正在查询，请稍候...</p>
        </div>
        <div v-else-if="error" class="result-box error">
          <p>{{ error }}</p>
        </div>
        <div v-else-if="report" class="result-box">
          <div class="result-header">
            <img class="result-logo" src="/images/logo.png" alt="logo" />
            <div class="result-title">报告信息</div>
          </div>
          <div class="result-grid">
            <div class="result-row">
              <span class="label">样品名称：</span>
              <span class="value">{{ report.sampleName || '/' }}</span>
            </div>
            <div class="result-row">
              <span class="label">报告编号：</span>
              <span class="value">{{ report.sampleNumber || '/' }}</span>
            </div>
            <div class="result-row">
              <span class="label">检验状态：</span>
              <span class="value">{{ report.inspectionStatus || '/' }}</span>
            </div>
            <div class="result-row">
              <span class="label">检验类型：</span>
              <span class="value">{{ report.inspectionType || '/' }}</span>
            </div>
            <div class="result-row">
              <span class="label">委托单位：</span>
              <span class="value">{{ report.clientUnit || '/' }}</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, nextTick, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'

export default {
  setup() {
    const route = useRoute()
    const router = useRouter()
    const isDetailMode = computed(() => route.name === 'VerificationDetail')
    const reportId = ref('')
    const loading = ref(false)
    const error = ref('')
    const report = ref(null)
    const qrCanvas = ref(null)
    const qrDataUrl = ref('')
    const qrReady = ref(false)

    const drawQRCode = async (text) => {
      try {
        const mod = await import('qrcode')
        const QRCode = mod.default || mod
        await nextTick()
        try {
          qrDataUrl.value = await QRCode.toDataURL(text, {
            width: 160,
            margin: 2,
            color: { dark: '#000000', light: '#FFFFFF' }
          })
          qrReady.value = true
        } catch (_) {
          if (qrCanvas.value) {
            await QRCode.toCanvas(qrCanvas.value, text, {
              width: 160,
              margin: 2,
              color: { dark: '#000000', light: '#FFFFFF' }
            })
            qrReady.value = true
          }
        }
      } catch (e) {
        console.error('生成二维码失败', e)
      }
    }

    const loadReport = async (id) => {
      if (!id) return
      const queryId = String(id).trim()
      if (!queryId) return
      loading.value = true
      error.value = ''
      report.value = null
      try {
        const ts = Date.now()
        // 先按ID直查
        const res = await fetch(`/api/report/${encodeURIComponent(queryId)}?_t=${ts}`)
        if (res.ok) {
          const data = await res.json()
          if (data && data.success && data.data) {
            report.value = data.data
          }
        }
        // 如果未命中或失败，退化为列表检索（支持按报告编号sampleNumber）
        if (!report.value) {
          const listRes = await fetch(`/api/reports?_t=${ts}`, { cache: 'no-cache' })
          if (listRes.ok) {
            const listData = await listRes.json()
            const arr = (listData && listData.data) || []
            const found = arr.find(r => {
              const rid = (r.id || '').toString()
              const sn = (r.sampleNumber || '').toString()
              return rid === queryId || sn === queryId
            })
            if (found) {
              report.value = found
            }
          }
        }
        // 最终处理
        if (report.value) {
          const ridValue = encodeURIComponent(report.value.sampleNumber || report.value.id)
          const url = `${window.location.origin}/verification?rid=${ridValue}`
          await drawQRCode(url)
        } else {
          error.value = '未查询到报告'
        }
      } catch (e) {
        console.error(e)
        error.value = '网络错误，请稍后重试'
      } finally {
        loading.value = false
      }
    }

    const handleVerify = () => {
      if (!reportId.value) {
        ElMessage.warning('请输入报告编号')
        return
      }
      loadReport(reportId.value)
      const query = new URLSearchParams({ rid: reportId.value }).toString()
      if (route.query.rid !== reportId.value) {
        router.replace(`/verification?${query}`)
      }
    }

    onMounted(() => {
      const rid = route.query.rid
      if (rid && typeof rid === 'string') {
        reportId.value = rid
        loadReport(rid)
      } else if (isDetailMode.value) {
        error.value = '缺少报告编号，请返回查询页输入报告编号'
      }
    })

    return {
      isDetailMode,
      reportId,
      loading,
      error,
      report,
      qrCanvas,
      qrDataUrl,
      qrReady,
      handleVerify
    }
  }
}
</script>

<style scoped>
.page-header {
  padding: 0;
  margin-bottom: 60px;
  color: #fff;
  text-align: center;
  position: relative;
  overflow: hidden;
  aspect-ratio: 4 / 1;
  display: flex;
  align-items: center;
}

.banner-picture {
  position: absolute;
  inset: 0;
  z-index: 0;
  display: block;
}

.banner-bg {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transform: none;
}

.banner-overlay {
  position: absolute;
  inset: 0;
  opacity: 0.08;
  background:
    radial-gradient(650px 360px at 15% 25%, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0) 62%),
    radial-gradient(620px 360px at 85% 45%, rgba(255, 214, 102, 0.04) 0%, rgba(255, 214, 102, 0) 64%),
    linear-gradient(135deg, rgba(125, 74, 199, 0.40) 0%, rgba(74, 20, 140, 0.34) 100%);
  z-index: 1;
}

.banner-overlay::before {
  content: '';
  position: absolute;
  inset: -25%;
  background:
    radial-gradient(closest-side at 18% 30%, rgba(255, 255, 255, 0.18), rgba(255, 255, 255, 0)),
    radial-gradient(closest-side at 82% 42%, rgba(255, 189, 92, 0.18), rgba(255, 189, 92, 0)),
    radial-gradient(closest-side at 55% 90%, rgba(160, 132, 255, 0.18), rgba(160, 132, 255, 0));
  filter: blur(22px);
  opacity: 0.9;
  animation: pageBannerBlobs 18s ease-in-out infinite alternate;
}

.banner-overlay::after {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 10px 10px, rgba(255, 255, 255, 0.14) 2px, rgba(255, 255, 255, 0) 3px),
    radial-gradient(circle at 48px 60px, rgba(255, 255, 255, 0.10) 1px, rgba(255, 255, 255, 0) 2px);
  background-size: 70px 70px, 140px 140px;
  opacity: 0.18;
  animation: pageBannerParticles 26s linear infinite;
  pointer-events: none;
}

.page-header .container {
  display: none;
}

@media (max-width: 768px) {
  .page-header {
    aspect-ratio: 4 / 5;
  }
}

.page-header h1 {
  margin: 0 0 15px 0;
  font-size: 2.5rem;
  font-weight: 700;
  animation: pageFadeUp 700ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

.subtitle {
  font-size: 1.2rem;
  opacity: 0.9;
  margin: 0;
  letter-spacing: 1px;
  animation: pageFadeUp 800ms 120ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

@keyframes pageFadeUp {
  from { opacity: 0; transform: translate3d(0, 10px, 0); }
  to { opacity: 1; transform: translate3d(0, 0, 0); }
}

@keyframes pageBannerZoom {
  from { transform: scale(1.06); }
  to { transform: scale(1.12); }
}

@keyframes pageBannerBlobs {
  0% { transform: translate3d(-1%, -1%, 0) scale(1); }
  50% { transform: translate3d(2%, 2%, 0) scale(1.03); }
  100% { transform: translate3d(-2%, 3%, 0) scale(1.02); }
}

@keyframes pageBannerParticles {
  from { background-position: 0 0, 0 0; }
  to { background-position: 0 -420px, 0 -840px; }
}

@media (prefers-reduced-motion: reduce) {
  .banner-bg,
  .banner-overlay::before,
  .banner-overlay::after,
  .page-header h1,
  .subtitle {
    animation: none !important;
  }
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.content {
  padding-bottom: 80px;
  display: flex;
  justify-content: center;
}

.verification-box {
  width: 100%;
  max-width: 700px;
  padding: 60px;
  background-color: #fff;
  box-shadow: 0 10px 40px rgba(0,0,0,0.08);
  border-radius: 12px;
  text-align: center;
  border: 1px solid #f0f0f0;
  opacity: 0;
  animation: contentFadeUp 700ms 120ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

.icon-wrapper {
  width: 80px;
  height: 80px;
  background-color: #f8f4fc;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 30px;
}

.search-icon {
  font-size: 40px;
}

.verification-box h2 {
  font-size: 1.8rem;
  color: #333;
  margin-bottom: 15px;
}

.desc {
  color: #666;
  margin-bottom: 40px;
}

.search-form {
  display: flex;
  margin-bottom: 50px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.05);
  border-radius: 8px;
  overflow: hidden;
}

.search-input {
  flex: 1;
  padding: 18px 25px;
  border: 1px solid #ddd;
  border-right: none;
  border-radius: 8px 0 0 8px;
  font-size: 16px;
  outline: none;
  transition: border-color 0.3s;
}

.search-input:focus {
  border-color: #7D4AC7;
}

.search-btn {
  padding: 0 40px;
  background-color: #7D4AC7;
  color: #fff;
  border: none;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 0 8px 8px 0;
  transition: background-color 0.3s, transform 0.2s;
  will-change: transform;
}

.search-btn:hover {
  background-color: #6A1B9A;
  transform: translateY(-1px);
}

.tips-box {
  background-color: #f9f9f9;
  padding: 30px;
  border-radius: 8px;
  text-align: left;
}

.tips-box h3 {
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
}

.tips-box h3::before {
  content: "💡";
  margin-right: 10px;
}

.tips-box ul {
  padding-left: 20px;
  margin: 0;
  color: #666;
}

.tips-box li {
  margin-bottom: 10px;
  line-height: 1.6;
}

.detail-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
}

.detail-pill {
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: #2f1a52;
  background: linear-gradient(135deg, rgba(125, 74, 199, 0.16) 0%, rgba(74, 20, 140, 0.12) 100%);
  border: 1px solid rgba(125, 74, 199, 0.22);
}

.detail-link {
  font-size: 14px;
  color: #7D4AC7;
  text-decoration: none;
}

.detail-link:hover {
  color: #6A1B9A;
}

.result-box {
  margin-top: 20px;
  text-align: left;
  padding: 20px;
  border: 1px solid #ece7f6;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 10px 30px rgba(125, 74, 199, 0.08);
  position: relative;
  opacity: 0;
  animation: contentFadeUp 700ms 200ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

.result-box.error {
  color: #c0392b;
}

.result-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.result-logo {
  width: 36px;
  height: 36px;
  object-fit: contain;
  border-radius: 6px;
}

.result-title {
  font-size: 18px;
  font-weight: 700;
  color: #3a2b5f;
}

.result-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-top: 8px;
}

.result-row {
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  border: 1px dashed #eee;
  border-radius: 8px;
  background: #faf9fd;
  transition: transform 0.25s, border-color 0.25s, background-color 0.25s;
  will-change: transform;
}

.result-row:hover {
  transform: translateY(-2px);
  border-color: #e0d4f5;
  background-color: #f7f3ff;
}

.result-row .label {
  color: #666;
  min-width: 90px;
}

.result-row .value {
  color: #333;
}

.qr-section {
  margin-top: 16px;
  text-align: center;
}

.qr-img {
  width: 160px;
  height: 160px;
  border-radius: 12px;
  border: 1px solid #ece7f6;
  background: #fff;
  box-shadow: 0 6px 18px rgba(125, 74, 199, 0.12);
  display: inline-block;
}

@keyframes contentFadeUp {
  from { opacity: 0; transform: translate3d(0, 12px, 0); }
  to { opacity: 1; transform: translate3d(0, 0, 0); }
}

@media (prefers-reduced-motion: reduce) {
  .verification-box,
  .result-box {
    animation: none !important;
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .verification-box {
    padding: 30px 20px;
  }
  
  .search-form {
    flex-direction: column;
    box-shadow: none;
  }
  
  .search-input {
    border: 1px solid #ddd;
    border-radius: 8px;
    margin-bottom: 15px;
    width: 100%;
    box-sizing: border-box;
  }
  
  .search-btn {
    border-radius: 8px;
    padding: 15px 0;
    width: 100%;
  }
}
</style>
